import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  CheckCircle, XCircle, Loader2, Play, Shield, Database, AlertTriangle, Lock
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const RBACPermissionsTestsSection = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const runTests = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/health/tests/rbac-permissions-flow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();
      setResults(data);
      
      if (data.failed === 0) {
        toast.success(`RBAC Permissions: All ${data.total} tests passed!`);
      } else {
        toast.warning(`RBAC Permissions: ${data.passed}/${data.total} tests passed`);
      }
    } catch (error) {
      toast.error('Failed to run RBAC permissions tests');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (passed) => {
    if (passed) return <CheckCircle className="w-5 h-5 text-green-500" />;
    return <XCircle className="w-5 h-5 text-red-500" />;
  };

  return (
    <Card className="border-orange-200 bg-orange-50/30">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-orange-600" />
            <CardTitle className="text-lg">RBAC Permissions Tests</CardTitle>
          </div>
          <Button 
            onClick={runTests} 
            disabled={loading}
            size="sm"
            className="bg-orange-600 hover:bg-orange-700"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running...</>
            ) : (
              <><Play className="w-4 h-4 mr-2" /> Run Tests</>
            )}
          </Button>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          Tests role-based access control middleware and permission enforcement
        </p>
      </CardHeader>
      
      <CardContent>
        {!results ? (
          <div className="text-center py-8 text-gray-500">
            <Shield className="w-12 h-12 mx-auto mb-3 text-orange-300" />
            <p>Click &quot;Run Tests&quot; to verify RBAC permissions</p>
            <p className="text-xs mt-2">Tests: Permission middleware, CRUD permissions, admin bypass, legacy format handling</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary */}
            <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  results.failed === 0 ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  {results.failed === 0 ? (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-600" />
                  )}
                </div>
                <div>
                  <p className="font-medium">{results.suite}</p>
                  <p className="text-sm text-gray-500">
                    {results.passed}/{results.total} passed ({results.pass_rate})
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                  {results.passed} Passed
                </span>
                {results.failed > 0 && (
                  <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded">
                    {results.failed} Failed
                  </span>
                )}
              </div>
            </div>

            {/* Test Results */}
            <div className="space-y-2">
              {results.tests?.map((test, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center justify-between p-2 rounded border ${
                    test.passed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-center gap-2 flex-1">
                    {getStatusIcon(test.passed)}
                    <span className="font-medium text-sm">{test.test}</span>
                    {test.uses_postgres && (
                      <Database className="w-3 h-3 text-blue-500" title="Uses PostgreSQL" />
                    )}
                  </div>
                  <span className="text-xs text-gray-500 max-w-md truncate" title={test.details}>
                    {test.details}
                  </span>
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 text-xs text-gray-500 mt-2 pt-2 border-t">
              <span className="flex items-center gap-1">
                <Database className="w-3 h-3 text-blue-500" /> PostgreSQL Test
              </span>
              <span className="flex items-center gap-1">
                <Shield className="w-3 h-3 text-orange-500" /> Permission Logic Test
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RBACPermissionsTestsSection;
